import React, { useEffect, useRef, useState } from "react";

const COLORS = [
  { label: "Black", value: "#111827" },
  { label: "Heading blue", value: "#236fa1" },
  { label: "Dark gray", value: "#4b5563" },
  { label: "Red", value: "#b91c1c" },
  { label: "Green", value: "#15803d" },
];

const FONT_SIZES = [
  { label: "S", title: "Small (12px)", value: "12px" },
  { label: "M", title: "Normal (14px)", value: "14px" },
  { label: "L", title: "Large (16px)", value: "16px" },
  { label: "XL", title: "Heading (18px)", value: "18px" },
];

/**
 * Word-like editor that keeps existing HTML styles (color, font, bold, etc.).
 * Toolbar stays sticky; the document scrolls underneath.
 */
const StylePreservingDocumentEditor = ({
  html,
  onChange,
  readOnly = false,
  minHeight = 420,
  maxHeight = 560,
  fillHeight = false,
  onReady,
}) => {
  const surfaceRef = useRef(null);
  const scrollRef = useRef(null);
  const lastExternalHtml = useRef(null);
  const savedRangeRef = useRef(null);
  const [activeColor, setActiveColor] = useState("#236fa1");
  const [activeSize, setActiveSize] = useState("14px");
  const [marks, setMarks] = useState({
    bold: false,
    italic: false,
    underline: false,
  });

  const syncFromDom = () => {
    const el = surfaceRef.current;
    if (!el || readOnly) return;
    const next = el.innerHTML;
    lastExternalHtml.current = next;
    onChange(next);
  };

  const focusEditor = () => {
    const el = surfaceRef.current;
    if (!el) return;
    el.focus();
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (
      selection &&
      selection.rangeCount > 0 &&
      surfaceRef.current?.contains(selection.anchorNode)
    ) {
      savedRangeRef.current = selection.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const range = savedRangeRef.current;
    if (!range) return;
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const refreshMarks = () => {
    try {
      setMarks({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      });
    } catch (e) {
      // ignore
    }
  };

  const runCommand = (command, value = null) => {
    if (readOnly) return;
    focusEditor();
    restoreSelection();
    try {
      document.execCommand("styleWithCSS", false, true);
      document.execCommand(command, false, value);
    } catch (e) {
      // ignore unsupported commands
    }
    saveSelection();
    refreshMarks();
    syncFromDom();
  };

  const applyInlineStyle = (styleProp, styleValue) => {
    if (readOnly) return;
    focusEditor();
    restoreSelection();

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      // No selection: start a styled span for typing ahead
      try {
        document.execCommand("styleWithCSS", false, true);
        if (styleProp === "color") {
          document.execCommand("foreColor", false, styleValue);
        } else if (styleProp === "fontSize") {
          // Create a temporary span wrapper for upcoming text
          const span = document.createElement("span");
          span.style.fontSize = styleValue;
          span.appendChild(document.createTextNode("\u200b"));
          const range = selection?.getRangeAt?.(0);
          if (range) {
            range.insertNode(span);
            range.setStart(span.firstChild, 1);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
      } catch (e) {
        // ignore
      }
      saveSelection();
      syncFromDom();
      return;
    }

    try {
      document.execCommand("styleWithCSS", false, true);
      if (styleProp === "color") {
        document.execCommand("foreColor", false, styleValue);
      } else if (styleProp === "fontSize") {
        // fontSize command uses 1–7; wrap selection with real CSS instead
        const range = selection.getRangeAt(0);
        const span = document.createElement("span");
        span.style.fontSize = styleValue;
        span.appendChild(range.extractContents());
        range.insertNode(span);
        selection.removeAllRanges();
        const next = document.createRange();
        next.selectNodeContents(span);
        selection.addRange(next);
      }
    } catch (e) {
      // ignore
    }
    saveSelection();
    refreshMarks();
    syncFromDom();
  };

  const insertHtmlAtCursor = (snippet) => {
    if (readOnly || !snippet) return;
    focusEditor();
    restoreSelection();
    try {
      const ok = document.execCommand("insertHTML", false, snippet);
      if (!ok) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          const temp = document.createElement("div");
          temp.innerHTML = snippet;
          const frag = document.createDocumentFragment();
          let node;
          let lastNode;
          while ((node = temp.firstChild)) {
            lastNode = frag.appendChild(node);
          }
          range.insertNode(frag);
          if (lastNode) {
            range.setStartAfter(lastNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } else if (surfaceRef.current) {
          surfaceRef.current.innerHTML += snippet;
        }
      }
    } catch (e) {
      if (surfaceRef.current) {
        surfaceRef.current.innerHTML += snippet;
      }
    }
    saveSelection();
    syncFromDom();
  };

  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    if (html === lastExternalHtml.current) return;
    el.innerHTML = html || "";
    lastExternalHtml.current = html || "";
  }, [html]);

  const apiRef = useRef({});
  apiRef.current.insertHtmlAtCursor = insertHtmlAtCursor;
  apiRef.current.focus = focusEditor;
  apiRef.current.getHtml = () => surfaceRef.current?.innerHTML || "";

  useEffect(() => {
    if (typeof onReady === "function") {
      onReady({
        insertHtmlAtCursor: (...args) =>
          apiRef.current.insertHtmlAtCursor?.(...args),
        focus: () => apiRef.current.focus?.(),
        getHtml: () => apiRef.current.getHtml?.() || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ToolbarButton = ({ label, title, onClick, active }) => (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={Boolean(active)}
      onMouseDown={(e) => {
        e.preventDefault();
        saveSelection();
        onClick();
      }}
      disabled={readOnly}
      className={`min-w-[2rem] h-8 px-2 inline-flex items-center justify-center text-sm rounded-md border transition-colors ${
        active
          ? "bg-blue-dark text-white border-blue-dark"
          : "bg-white border-gray-200 text-gray-800 hover:bg-gray-50 hover:border-gray-300"
      } disabled:opacity-50`}
    >
      {label}
    </button>
  );

  const stopScrollChaining = (event) => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const delta = event.deltaY;
    const atTop = scrollTop <= 0;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 1;

    if ((delta < 0 && atTop) || (delta > 0 && atBottom)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const handler = (event) => stopScrollChaining(event);
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  return (
    <div
      className={`border border-gray-200 rounded-md overflow-hidden bg-white flex flex-col ${
        fillHeight ? "h-full min-h-0" : ""
      }`}
    >
      <div className="shrink-0 z-10 border-b border-gray-200 bg-white">
        <div className="px-3 py-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mr-1 hidden sm:inline">
            Format
          </span>
          <ToolbarButton
            label={<strong>B</strong>}
            title="Bold (Ctrl+B)"
            active={marks.bold}
            onClick={() => runCommand("bold")}
          />
          <ToolbarButton
            label={<em>I</em>}
            title="Italic (Ctrl+I)"
            active={marks.italic}
            onClick={() => runCommand("italic")}
          />
          <ToolbarButton
            label={<span className="underline">U</span>}
            title="Underline (Ctrl+U)"
            active={marks.underline}
            onClick={() => runCommand("underline")}
          />

          <span className="w-px h-6 bg-gray-200 mx-1" />

          <ToolbarButton
            label="•"
            title="Bullet list"
            onClick={() => runCommand("insertUnorderedList")}
          />
          <ToolbarButton
            label="1."
            title="Numbered list"
            onClick={() => runCommand("insertOrderedList")}
          />

          <span className="w-px h-6 bg-gray-200 mx-1" />

          <ToolbarButton
            label="⟸"
            title="Align left"
            onClick={() => runCommand("justifyLeft")}
          />
          <ToolbarButton
            label="≡"
            title="Align centre"
            onClick={() => runCommand("justifyCenter")}
          />
          <ToolbarButton
            label="☰"
            title="Justify"
            onClick={() => runCommand("justifyFull")}
          />

          <span className="w-px h-6 bg-gray-200 mx-1" />

          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 hidden sm:inline mr-0.5">
              Size
            </span>
            {FONT_SIZES.map((opt) => (
              <ToolbarButton
                key={opt.value}
                label={opt.label}
                title={opt.title}
                active={activeSize === opt.value}
                onClick={() => {
                  setActiveSize(opt.value);
                  applyInlineStyle("fontSize", opt.value);
                }}
              />
            ))}
          </div>

          <span className="w-px h-6 bg-gray-200 mx-1" />

          <div className="flex items-center gap-1">
            <span className="text-[11px] text-gray-500 hidden sm:inline mr-0.5">
              Colour
            </span>
            {COLORS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                title={opt.label}
                aria-label={`Text colour ${opt.label}`}
                disabled={readOnly}
                onMouseDown={(e) => {
                  e.preventDefault();
                  saveSelection();
                  setActiveColor(opt.value);
                  applyInlineStyle("color", opt.value);
                }}
                className={`h-7 w-7 rounded-full border-2 transition ${
                  activeColor === opt.value
                    ? "border-blue-dark ring-2 ring-blue-100"
                    : "border-white shadow-sm hover:scale-105"
                } disabled:opacity-50`}
                style={{ backgroundColor: opt.value }}
              />
            ))}
          </div>

          <span className="w-px h-6 bg-gray-200 mx-1" />

          <ToolbarButton
            label="↶"
            title="Undo"
            onClick={() => runCommand("undo")}
          />
          <ToolbarButton
            label="↷"
            title="Redo"
            onClick={() => runCommand("redo")}
          />
        </div>
        <p className="px-3 pb-2 text-[11px] text-gray-500">
          Select text, then format. Scroll only happens inside this document —
          the page will not jump.
        </p>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-gray-50"
        style={
          fillHeight
            ? { overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }
            : {
                maxHeight,
                minHeight,
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
              }
        }
      >
        <div
          ref={surfaceRef}
          className="sa-doc-editor mx-auto my-3 px-5 py-4 bg-white shadow-sm border border-gray-100 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
          style={{ minHeight: fillHeight ? "100%" : Math.max(minHeight - 24, 360), maxWidth: 820 }}
          contentEditable={!readOnly}
          suppressContentEditableWarning
          onInput={() => {
            saveSelection();
            refreshMarks();
            syncFromDom();
          }}
          onBlur={syncFromDom}
          onMouseUp={() => {
            saveSelection();
            refreshMarks();
          }}
          onKeyUp={() => {
            saveSelection();
            refreshMarks();
          }}
          role="textbox"
          aria-multiline="true"
          aria-label="Service agreement document editor"
        />
      </div>

      <style>{`
        .sa-doc-editor {
          font-family: Roboto, Arial, sans-serif;
          font-size: 14px;
          line-height: 1.5;
          color: #111827;
        }
        .sa-doc-editor .prow { margin-bottom: 0.35rem; }
        .sa-doc-editor ul, .sa-doc-editor ol { padding-left: 1.5rem; margin: 0.5rem 0; }
        .sa-doc-editor p { margin: 0.4rem 0; }
        .sa-doc-editor table { border-collapse: collapse; width: 100%; }
        .sa-doc-editor table td, .sa-doc-editor table th {
          border: 1px solid #d1d5db;
          padding: 4px 6px;
        }
        .sa-doc-editor:focus { outline: none; }
      `}</style>
    </div>
  );
};

export default StylePreservingDocumentEditor;
